import React from 'react';
import { Box, Heading, Text, Button, VStack, HStack, Code, Collapse, useDisclosure } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);

    // Log to console with more details
    if (error.message && error.message.includes('Sequelize')) {
      console.info('Sequelize error detected - this may be related to page navigation');
    }

    if (error.message && error.message.includes('MIME type')) {
      console.info('MIME type error detected - this may be related to asset loading');
    }

    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  }

  // Handle home navigation with a full page reload to avoid potential issues
  handleHomeClick = () => {
    window.location.href = '/';
  }

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state;

      // Check for specific error types
      const isSequelizeError = error && error.message && error.message.includes('Sequelize');
      const isMimeTypeError = error && error.message && error.message.includes('MIME type');

      return (
        <Box p={8} textAlign="center" maxW="800px" mx="auto">
          <Heading mb={4} color="#5797d9">Something went wrong</Heading>

          <Text mb={6} fontSize="lg">
            We've encountered an error. This might be a temporary issue.
          </Text>

          <VStack spacing={4} mb={8}>
            <Button
              colorScheme="blue"
              size="lg"
              onClick={() => window.location.reload()}
              w={{ base: "full", md: "auto" }}
            >
              Refresh Page
            </Button>

            <Button
              variant="outline"
              colorScheme="blue"
              onClick={this.handleHomeClick}
              w={{ base: "full", md: "auto" }}
            >
              Return to Home
            </Button>
          </VStack>

          {/* Conditional messaging based on error type */}
          {isSequelizeError && (
            <Box bg="blue.50" p={4} borderRadius="md" mb={4}>
              <Text color="blue.800">
                This appears to be a database-related error. Our team has been notified.
              </Text>
            </Box>
          )}

          {isMimeTypeError && (
            <Box bg="blue.50" p={4} borderRadius="md" mb={4}>
              <Text color="blue.800">
                This appears to be a resource loading error. Clearing your browser cache might help.
              </Text>
            </Box>
          )}

          {/* Technical details (hidden by default) */}
          <Box mt={8}>
            <Button
              variant="link"
              colorScheme="gray"
              onClick={this.toggleDetails}
              size="sm"
            >
              {showDetails ? "Hide Technical Details" : "Show Technical Details"}
            </Button>

            <Collapse in={showDetails}>
              <Box
                mt={4}
                p={4}
                bg="gray.50"
                borderRadius="md"
                textAlign="left"
                overflowX="auto"
              >
                <Text fontWeight="bold" mb={2}>Error:</Text>
                <Code p={2} borderRadius="md" w="full" display="block" whiteSpace="pre-wrap">
                  {error && (error.toString() || 'Unknown error')}
                </Code>

                {errorInfo && errorInfo.componentStack && (
                  <>
                    <Text fontWeight="bold" mt={4} mb={2}>Component Stack:</Text>
                    <Code p={2} borderRadius="md" w="full" display="block" whiteSpace="pre-wrap" fontSize="xs">
                      {errorInfo.componentStack}
                    </Code>
                  </>
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
